import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="combobox"
// Turns a text input into an autocomplete combobox that fetches suggestions from a URL.
//
// Values:
//   url:       The autocomplete endpoint URL (e.g. "/autocomplete/clients")
//   param-key: The query param key to send (e.g. "q[name_unaccent_cont]")
//   min-length: Minimum characters before fetching (default: 3)
//
// Targets:
//   input: The text input element
//
export default class extends Controller {
  static targets = ["input", "hiddenField", "optionTemplate"]
  static values = {
    url: String,
    paramKey: String,
    minLength: { type: Number, default: 3 }
  }

  connect() {
    this.selectedIndex = -1
    this.results = []
    this.buildDropdown()
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    this.boundHandleFormSubmit = this.handleFormSubmit.bind(this)
    this.form = this.inputTarget.closest("form")

    this.form?.addEventListener("submit", this.boundHandleFormSubmit)
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleClickOutside)
    this.form?.removeEventListener("submit", this.boundHandleFormSubmit)
    if (this.dropdown) this.dropdown.remove()

    this.inputTarget.disabled = false

    // Unwrap the input back to its original position
    if (this.wrapper && this.wrapper.parentElement) {
      this.wrapper.parentElement.insertBefore(this.inputTarget, this.wrapper)
      this.wrapper.remove()
    }
  }

  buildDropdown() {
    // Wrap the input in a relative container so the dropdown positions right below it
    this.wrapper = document.createElement("div")
    this.wrapper.className = "relative w-full"
    this.inputTarget.parentElement.insertBefore(this.wrapper, this.inputTarget)
    this.wrapper.appendChild(this.inputTarget)

    this.dropdown = document.createElement("div")
    this.dropdown.className = "hidden absolute left-0 top-full z-50 mt-1 w-full bg-base-100 border rounded-box shadow-xl max-h-64 overflow-y-auto"
    this.wrapper.appendChild(this.dropdown)
  }

  onInput() {
    // Clear the hidden ID field when the user types
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = ""
    }

    this.inputTarget.disabled = false

    const query = this.inputTarget.value.trim()

    if (query.length < this.minLengthValue) {
      this.close()
      return
    }

    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  onKeydown(event) {
    if (this.dropdown.classList.contains("hidden")) return

    const items = this.dropdown.querySelectorAll("[data-combobox-option]")
    if (items.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlightOption(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.highlightOption(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          this.selectOption(items[this.selectedIndex].dataset.value, items[this.selectedIndex].dataset.id)
        }
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  async fetchResults(query) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set(this.paramKeyValue, query)

    try {
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) return

      this.results = await response.json()
      this.renderResults()
    } catch (e) {
      // Silently fail on network errors
    }
  }

  renderResults() {
    this.dropdown.innerHTML = ""
    this.selectedIndex = -1

    if (this.results.length === 0) {
      this.dropdown.innerHTML = `
        <div class="px-3 py-4 text-base-content/60 text-sm text-center">
          Nenhum resultado encontrado
        </div>
      `
      this.open()
      return
    }

    this.results.forEach(item => {
      const id = item.id
      const value = item.value
      const label = item.label

      const option = document.createElement("div")
      option.dataset.comboboxOption = ""
      option.dataset.value = value
      option.dataset.id = id
      option.className = this.optionClassName()

      if (this.hasOptionTemplateTarget) {
        option.appendChild(this.renderOptionTemplate(item))
      } else {
        option.textContent = label
      }

      option.addEventListener("click", () => this.selectOption(value, id))
      this.dropdown.appendChild(option)
    })

    this.open()
  }

  selectOption(value, id) {
    this.inputTarget.value = value
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = id || ""
    }
    this.close()
    this.form?.requestSubmit()
  }

  handleFormSubmit() {
    if (!this.hasHiddenFieldTarget) return

    this.inputTarget.disabled = this.hiddenFieldTarget.value.trim() !== ""
  }

  optionClassName() {
    const baseClassName = "hover:bg-primary/20 cursor-pointer transition-colors"

    if (this.hasOptionTemplateTarget) {
      return baseClassName
    }

    return `${baseClassName} px-3 py-2 text-sm`
  }

  renderOptionTemplate(item) {
    const fragment = this.optionTemplateTarget.content.cloneNode(true)

    fragment.querySelectorAll("[data-field]").forEach((element) => {
      const value = item[element.dataset.field]

      if (value === undefined || value === null || value === "") {
        element.remove()
        return
      }

      element.textContent = value
    })

    return fragment
  }

  highlightOption(items) {
    items.forEach((item, index) => {
      item.classList.toggle("bg-base-300", index === this.selectedIndex)
    })

    if (items[this.selectedIndex]) {
      items[this.selectedIndex].scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }

  open() {
    this.dropdown.classList.remove("hidden")
    document.addEventListener("click", this.boundHandleClickOutside)
  }

  close() {
    this.dropdown.classList.add("hidden")
    this.selectedIndex = -1
    document.removeEventListener("click", this.boundHandleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }
}
